import { supabase } from "@/integrations/supabase/client";
import { User, UserRole, PaginatedResponse, PaginationParams } from "@/types";
import { Json } from "@/integrations/supabase/types";
import { filterUsersBySubscription } from "./filterUsersBySubscription";

// Helper function to get current user's profile ID
export const getCurrentUserProfileId = async (): Promise<number> => {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error('User not authenticated');

  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id')
    .eq('uid', user.user.id)
    .single();

  if (profileError || !profile) {
    throw new Error('User profile not found');
  }

  return profile.id;
};

// Database result interfaces
interface UserToRoleResult {
  role_id: number;
  user_roles: {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    is_system: boolean;
  };
}

interface UserDatabaseResult {
  id: number;
  uid: string;
  name: string;
  email: string;
  avatar_url: string | null;
  age_group: string | null;
  is_active: boolean;
  registered_at: string;
  last_login_at?: string | null;
  referral_code?: string | null;
  referred_by?: number | null;
  user_to_roles: UserToRoleResult[];
  referrer?: {
    name: string;
  } | null;
}

// Fetch a single user by ID
export const fetchUser = async (id: number): Promise<User | null> => {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .select(
        `
        *,
        user_to_roles (
          role_id,
          user_roles (
            id, name, description, is_active, is_system
          )
        ),
        referrer:referred_by(name)
      `
      )
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching user:", error);
      throw error;
    }

    if (!data) return null;

    // Transform the roles data into the expected format
    const roles = data.user_to_roles.map((ur: UserToRoleResult) => ({
      id: ur.user_roles.id,
      name: ur.user_roles.name,
      description: ur.user_roles.description,
      isActive: ur.user_roles.is_active,
      isSystem: ur.user_roles.is_system,
    }));

    const roleIds = roles.map((role: UserRole) => role.id);

    // Return the user with the transformed data
    return {
      id: data.id,
      uid: data.uid,
      name: data.name,
      email: data.email,
      avatarUrl: data.avatar_url,
      ageGroup: data.age_group,
      isActive: data.is_active,
      registeredAt: data.registered_at,
      roleIds,
      roles,
      referralCode: data.referral_code,
      referredBy: data.referred_by,
      referredByName: data.referrer?.name,
    };
  } catch (error) {
    console.error("Error in fetchUser:", error);
    throw error;
  }
};

// Fetch users with pagination and sorting
export const fetchUsers = async (
  { page = 1, pageSize = 10 }: PaginationParams,
  filters: Record<string, string> = {},
  sort?: { column: string; direction: "asc" | "desc" }
): Promise<PaginatedResponse<User>> => {
  try {
    // Handle role filter separately if provided
    let roleFilter = false;
    let roleId: number | undefined;

    if (filters.role) {
      roleFilter = true;
      roleId = parseInt(filters.role, 10);
    }

    // Extract plan filter for later use
    const planFilter = filters.plan || "";
    const expiryFilter = filters.expiry || "";

    // Helper: derive user IDs by expiry status from user_subscriptions
    const deriveUserIdsByExpiry = async (): Promise<{
      includeIds?: number[];
      excludeIds?: number[];
    }> => {
      if (!expiryFilter) return {};

      const now = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      // Fetch latest subscription per user by created_at (most recent first)
      const { data: subs, error: subsError } = await supabase
        .from("user_subscriptions")
        .select("user_id, status, subscription_end, created_at")
        .order("created_at", { ascending: false });

      if (subsError) {
        console.error("Error fetching subscriptions for expiry filter:", subsError);
        throw subsError;
      }

      const latestByUser = new Map<number, { status: string; end: string | null }>();
      (subs || []).forEach((row: { user_id: number; status: string; subscription_end: string | null }) => {
        if (!latestByUser.has(row.user_id)) {
          latestByUser.set(row.user_id, { status: row.status, end: row.subscription_end || null });
        }
      });

      const includeIds: number[] = [];
      const allWithAnySubscription: number[] = Array.from(latestByUser.keys());

      if (expiryFilter === "none") {
        // We will exclude all users who have any subscription
        return { excludeIds: allWithAnySubscription };
      }

      latestByUser.forEach(({ status, end }, userId) => {
        const hasEnd = !!end;
        const expiryDate = hasEnd ? new Date(end as string) : null;
        switch (expiryFilter) {
          case "expired":
            if ((status === "expired") || (expiryDate !== null && expiryDate < now)) includeIds.push(userId);
            break;
          case "expiring-soon":
            if (expiryDate && expiryDate >= now && expiryDate <= oneMonthFromNow)
              includeIds.push(userId);
            break;
          case "active":
            // Treat non-expired as active (includes expiring-soon); if no end date, assume active
            if ((status === "active" && !expiryDate) || (expiryDate && expiryDate >= now)) includeIds.push(userId);
            break;
        }
      });

      return { includeIds };
    };

    // Create the query with proper type assertions for Supabase client
    let query;
    if (roleFilter && roleId) {
      // Optionally narrow by expiry before querying profiles
      let includeIds: number[] | undefined;
      let excludeIds: number[] | undefined;
      if (expiryFilter) {
        const derived = await deriveUserIdsByExpiry();
        includeIds = derived.includeIds;
        excludeIds = derived.excludeIds;
        // If includeIds specified but empty, we can short-circuit
        if (includeIds && includeIds.length === 0) {
          return {
            data: [],
            total: 0,
            page,
            pageSize,
            totalPages: 0,
          };
        }
      }

      // Build role-scoped query and apply expiry constraints
      let roleQuery = supabase
        .from("profiles")
        .select(
          `
          *,
          user_to_roles!inner (
            role_id,
            user_roles (
              id, name, description, is_active, is_system
            )
          ),
          referrer:referred_by(name)
        `,
          { count: "exact" }
        )
        .eq("user_to_roles.role_id", roleId);

      // Apply search filter if provided (on profiles columns)
      if (filters.search) {
        roleQuery = roleQuery.or(
          `name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
        );
      }

      // Apply status filter if provided
      if (filters.status) {
        if (filters.status === "pending") {
          roleQuery = roleQuery.eq("name", "New User");
        } else {
          roleQuery = roleQuery
            .eq("is_active", filters.status === "active")
            .neq("name", "New User");
        }
      }

      // Apply sorting if provided
      try {
        if (sort) {
          const columnMap: Record<string, string> = {
            name: "name",
            email: "email",
            isActive: "is_active",
            registeredAt: "registered_at",
            lastLoginAt: "last_login_at",
          };

          if (columnMap[sort.column]) {
            roleQuery = roleQuery.order(columnMap[sort.column], {
              ascending: sort.direction === "asc",
            });
          }
        } else {
          roleQuery = roleQuery.order("registered_at", { ascending: false });
        }
      } catch (sortError) {
        console.warn("Error applying sort (role scope)", sortError);
      }

      if (expiryFilter) {
        if (includeIds && includeIds.length > 0) {
          roleQuery = roleQuery.in("id", includeIds);
        } else if (excludeIds && excludeIds.length > 0) {
          roleQuery = roleQuery.not("id", "in", `(${excludeIds.join(",")})`);
        }
      }

      roleQuery = roleQuery.range(
        (page - 1) * pageSize,
        (page - 1) * pageSize + pageSize - 1
      );

      const { data, error, count } = await roleQuery;

      if (error) {
        console.error("Error fetching users:", error);
        throw error;
      }

      // Transform the data directly here
      const users = transformUserData(data || []);

      // Apply subscription plan filtering if needed
      let totalUsers = count || 0;
      let filteredUsers = users;

      if (planFilter) {
        filteredUsers = await filterUsersBySubscription(users, planFilter);
        totalUsers = filteredUsers.length;
      }

      return {
        data: filteredUsers,
        total: totalUsers,
        page,
        pageSize,
        totalPages: Math.ceil(totalUsers / pageSize),
      };
    } else {
      // Build query for users without role filtering
      // Apply filters for search/status
      let queryBuilder = supabase.from("profiles").select(
        `
          *,
          user_to_roles (
            role_id,
            user_roles (
              id, name, description, is_active, is_system
            )
          ),
          referrer:referred_by(name)
        `,
        { count: "exact" }
      );

      // Apply expiry filter by narrowing user IDs from subscriptions first
      if (expiryFilter) {
        const { includeIds, excludeIds } = await deriveUserIdsByExpiry();
        // Short-circuit if include list is empty
        if (includeIds && includeIds.length === 0) {
          return {
            data: [],
            total: 0,
            page,
            pageSize,
            totalPages: 0,
          };
        }

        if (includeIds && includeIds.length > 0) {
          queryBuilder = queryBuilder.in("id", includeIds);
        } else if (excludeIds && excludeIds.length > 0) {
          queryBuilder = queryBuilder.not("id", "in", `(${excludeIds.join(",")})`);
        }
      }

      // Apply search filter if provided
      if (filters.search) {
        queryBuilder = queryBuilder.or(
          `name.ilike.%${filters.search}%,email.ilike.%${filters.search}%`
        );
      }

      // Apply status filter if provided
      if (filters.status) {
        if (filters.status === "pending") {
          queryBuilder = queryBuilder.eq("name", "New User");
        } else {
          queryBuilder = queryBuilder
            .eq("is_active", filters.status === "active")
            .neq("name", "New User");
        }
      }

      // Apply sorting if possible
      try {
        if (sort) {
          // Map frontend field names to database column names
          const columnMap: Record<string, string> = {
            name: "name",
            email: "email",
            isActive: "is_active",
            registeredAt: "registered_at",
            lastLoginAt: "last_login_at",
          };

          if (columnMap[sort.column]) {
            queryBuilder = queryBuilder.order(columnMap[sort.column], {
              ascending: sort.direction === "asc",
            });
          }
        } else {
          // Default sort
          queryBuilder = queryBuilder.order("registered_at", {
            ascending: false,
          });
        }
      } catch (sortError) {
        console.warn(
          "Error applying sort, continuing without sorting:",
          sortError
        );
      }

      // Apply pagination
      queryBuilder = queryBuilder.range(
        (page - 1) * pageSize,
        (page - 1) * pageSize + pageSize - 1
      );

      // Execute the query
      const { data, error, count } = await queryBuilder;

      if (error) {
        console.error("Error fetching users:", error);
        throw error;
      }

      // Transform the data
      const users = transformUserData(data || []);

      // Apply subscription plan filtering if needed
      let totalUsers = count || 0;
      let filteredUsers = users;

      if (planFilter) {
        filteredUsers = await filterUsersBySubscription(users, planFilter);
        totalUsers = filteredUsers.length;
      }

      return {
        data: filteredUsers,
        total: totalUsers,
        page,
        pageSize,
        totalPages: Math.ceil(totalUsers / pageSize),
      };
    }
  } catch (error) {
    console.error("Error in fetchUsers:", error);
    throw error;
  }
};

// Helper function to transform the user data from Supabase format to our app format
function transformUserData(userData: UserDatabaseResult[]): User[] {
  try {
    return userData.map((user) => {
      // Handle cases where user_to_roles might be null or not an array
      const userToRoles = Array.isArray(user.user_to_roles)
        ? user.user_to_roles
        : [];

      const roles = userToRoles.map((ur: UserToRoleResult) => ({
        id: ur.user_roles?.id || 0,
        name: ur.user_roles?.name || "Unknown",
        description: ur.user_roles?.description || "",
        isActive: ur.user_roles?.is_active || false,
        isSystem: ur.user_roles?.is_system || false,
      }));

      const roleIds = roles.map((role: UserRole) => role.id);

      return {
        id: user.id,
        uid: user.uid || "",
        name: user.name || "Unknown User",
        email: user.email || "",
        avatarUrl: user.avatar_url || "",
        ageGroup: user.age_group,
        isActive: user.is_active || false,
        registeredAt: user.registered_at || new Date().toISOString(),
        lastLoginAt: user.last_login_at || null,
        roleIds,
        roles,
        referralCode: user.referral_code,
        referredBy: user.referred_by,
        referredByName: user.referrer?.name,
      };
    });
  } catch (error) {
    console.error("Error transforming user data:", error);
    return [];
  }
}

// Create a new user via Supabase Edge Function
export const createUserWithAuth = async (userData: {
  name: string;
  email: string;
  avatarUrl?: string;
  ageGroup?: string;
  isActive: boolean;
  roleIds: number[];
}): Promise<User> => {
  try {
    // Call the users/create Edge Function using Supabase client
    const { data: result, error } = await supabase.functions.invoke(
      "users/create",
      {
        body: userData,
      }
    );

    if (error) {
      throw new Error(error.message || "Failed to create user");
    }

    // Now fetch the complete user to return
    const createdUser = await fetchUser(result.user.id);
    if (!createdUser) {
      throw new Error("User created but could not fetch details");
    }

    return createdUser;
  } catch (error) {
    console.error("Error creating user with auth:", error);
    throw error;
  }
};

// Create a new user
export const createUser = async (userData: Omit<User, "id">): Promise<User> => {
  // Convert from our format to Supabase format
  const { roleIds, roles, ...rest } = userData;

  const supabaseUser = {
    id: undefined, // Let the database generate the ID
    name: rest.name,
    email: rest.email,
    avatar_url: rest.avatarUrl,
    is_active: rest.isActive,
    registered_at: rest.registeredAt,
    uid: rest.uid,
  };

  // Insert the user
  const { data: newUser, error: userError } = await supabase
    .from("profiles")
    .insert(supabaseUser)
    .select()
    .single();

  if (userError) {
    console.error("Error creating user:", userError);
    throw userError;
  }

  // Add role assignments if provided
  if (roleIds && roleIds.length > 0) {
    const roleEntries = roleIds.map((roleId) => ({
      user_id: newUser.id,
      role_id: roleId,
    }));

    const { error: rolesError } = await supabase
      .from("user_to_roles")
      .insert(roleEntries);

    if (rolesError) {
      console.error("Error assigning roles to user:", rolesError);
      throw rolesError;
    }
  }

  // Return the complete user with roles
  return fetchUser(newUser.id) as Promise<User>;
};

// Update a user with the new users API
export const updateUser = async (
  id: number,
  updates: Partial<User>
): Promise<User> => {
  try {
    const { roleIds, roles, ...profileUpdates } = updates;

    // Map frontend field names to database column names
    const mappedUpdates: Record<string, unknown> = {
      id, // Include ID for the endpoint
    };

    if ("name" in profileUpdates) mappedUpdates.name = profileUpdates.name;
    if ("email" in profileUpdates) mappedUpdates.email = profileUpdates.email;
    if ("avatarUrl" in profileUpdates)
      mappedUpdates.avatarUrl = profileUpdates.avatarUrl;
    if ("ageGroup" in profileUpdates)
      mappedUpdates.ageGroup = profileUpdates.ageGroup;
    if ("isActive" in profileUpdates)
      mappedUpdates.isActive = profileUpdates.isActive;

    // Include roleIds if provided
    if (roleIds) mappedUpdates.roleIds = roleIds;

    // Call the users/update Edge Function using Supabase client
    const { error } = await supabase.functions.invoke("users/update", {
      body: mappedUpdates,
    });

    if (error) {
      throw new Error(error.message || "Failed to update user");
    }

    // Fetch the updated user
    const updatedUser = await fetchUser(id);
    if (!updatedUser) {
      throw new Error("User updated but could not fetch details");
    }

    return updatedUser;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

// Delete a user with the new users API
export const deleteUser = async (
  id: number,
  reason: string,
  additional_feedback?: string
): Promise<void> => {
  try {
    // Call the users/delete Edge Function using Supabase client
    const { error } = await supabase.functions.invoke("users/delete", {
      body: { 
        id, 
        reason, 
        additional_feedback,
        user_agent: navigator.userAgent,
        platform: "web-admin"
      },
    });

    if (error) {
      throw new Error(error.message || "Failed to delete user");
    }
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

// Ban a user (set isActive to false)
export const banUser = async (id: number): Promise<void> => {
  try {
    // Call the users/ban Edge Function using Supabase client
    const { error } = await supabase.functions.invoke("users/ban", {
      body: { id },
    });

    if (error) {
      throw new Error(error.message || "Failed to ban user");
    }
  } catch (error) {
    console.error("Error banning user:", error);
    throw error;
  }
};

// Unban a user (set isActive to true)
export const unbanUser = async (id: number): Promise<void> => {
  try {
    // Call the users/unban Edge Function using Supabase client
    const { error } = await supabase.functions.invoke("users/unban", {
      body: { id },
    });

    if (error) {
      throw new Error(error.message || "Failed to unban user");
    }
  } catch (error) {
    console.error("Error unbanning user:", error);
    throw error;
  }
};

// Type definition for auth user info
interface AuthUserInfo {
  confirmed_at?: string;
  email_confirmed_at?: string;
  created_at: string;
  last_sign_in_at?: string;
  phone?: string;
  phone_confirmed_at?: string;
  is_anonymous?: boolean;
  app_metadata: Record<string, unknown>;
  user_metadata: Record<string, unknown>;
  aud?: string;
  role?: string;
  updated_at?: string;
  identities?: {
    provider: string;
    id: string;
    created_at: string;
    updated_at: string;
  }[];
}

// Get auth details for a user
export const fetchUserAuthDetails = async (
  userId?: number,
  uid?: string
): Promise<AuthUserInfo> => {
  try {
    // Now we'll modify our approach to match how the edge function expects parameters
    // We're going to adapt our client approach to how the server function works

    // First, let's check if we have at least one parameter
    if (!userId && !uid) {
      throw new Error("Either userId or uid must be provided");
    }

    // Generate the function path with parameters directly in the URL path
    let functionPath = "users/auth-details";

    // Add parameters to the path
    const params: string[] = [];
    if (userId) params.push(`userId=${userId}`);
    if (uid) params.push(`uid=${uid}`);

    if (params.length > 0) {
      functionPath += `?${params.join("&")}`;
    }

    // Call the edge function with the proper path
    const { data, error } = await supabase.functions.invoke(functionPath);

    if (error) {
      throw new Error(error.message || "Failed to fetch user auth details");
    }

    return data;
  } catch (error) {
    console.error("Error fetching user auth details:", error);
    throw error;
  }
};

// Reset user password
export const resetUserPassword = async (email: string): Promise<void> => {
  try {
    // Call the users/reset-password Edge Function using Supabase client
    const { error } = await supabase.functions.invoke("users/reset-password", {
      body: { email },
    });

    if (error) {
      throw new Error(error.message || "Failed to reset user password");
    }
  } catch (error) {
    console.error("Error resetting user password:", error);
    throw error;
  }
};

// Resend OTP (verification code)
export const resendOTP = async (email: string): Promise<void> => {
  try {
    // Call the users/resend-otp Edge Function using Supabase client
    const { error } = await supabase.functions.invoke("users/resend-otp", {
      body: { email },
    });

    if (error) {
      throw new Error(error.message || "Failed to send verification OTP");
    }
  } catch (error) {
    console.error("Error sending verification OTP:", error);
    throw error;
  }
};

// For backward compatibility, keep the old function as an alias
export const resendConfirmationEmail = resendOTP;

// Resend invitation email to a user
export const resendInvitation = async (userData: {
  name: string;
  email: string;
  avatarUrl?: string;
  ageGroup?: string;
  isActive: boolean;
  roleIds: number[];
}): Promise<void> => {
  try {
    // Call the dedicated resend-invitation Edge Function endpoint
    const { error } = await supabase.functions.invoke("users/resend-invitation", {
      body: {
        email: userData.email,
        name: userData.name,
      },
    });

    if (error) {
      throw new Error(error.message || "Failed to resend invitation");
    }
  } catch (error) {
    console.error("Error resending invitation:", error);
    throw error;
  }
};

// Manually confirm user's email
export const confirmUserEmail = async (uid: string): Promise<void> => {
  try {
    // Call the users/manually-confirm-email Edge Function using Supabase client
    const { error } = await supabase.functions.invoke(
      "users/manually-confirm-email",
      {
        body: { uid },
      }
    );

    if (error) {
      throw new Error(error.message || "Failed to confirm user email");
    }
  } catch (error) {
    console.error("Error confirming user email:", error);
    throw error;
  }
};

// Get user analytics for dashboard
export const fetchUsersAnalytics = async (): Promise<{
  total: number;
  active: number;
  pending: number;
  roles: Record<string, number>;
  recent: number;
  today: number;
  yesterday: number;
}> => {
  // Get total users
  const { count: totalCount, error: totalError } = await supabase
    .from("profiles")
    .select("*", { count: "exact", head: true });

  if (totalError) {
    console.error("Error fetching total users:", totalError);
    throw totalError;
  }

  // Get active users (excluding "New User" since they're counted as pending)
  const { count: activeCount, error: activeError } = await supabase
    .from("profiles")
    .select("*", { count: "exact", head: true })
    .eq("is_active", true)
    .neq("name", "New User");

  if (activeError) {
    console.error("Error fetching active users:", activeError);
    throw activeError;
  }

  // Get pending users (name === "New User")
  const { count: pendingCount, error: pendingError } = await supabase
    .from("profiles")
    .select("*", { count: "exact", head: true })
    .eq("name", "New User");

  if (pendingError) {
    console.error("Error fetching pending users:", pendingError);
    throw pendingError;
  }

  // Get users by role
  const { data: roleData, error: roleError } = await supabase
    .from("user_to_roles")
    .select(
      `
      role_id,
      user_roles (
        name
      )
    `
    );

  if (roleError) {
    console.error("Error fetching user roles:", roleError);
    throw roleError;
  }

  const roles: Record<string, number> = {};
  roleData.forEach((entry) => {
    // Need to access name through user_roles
    const roleName = entry.user_roles?.name as string;
    if (roleName) {
      roles[roleName] = (roles[roleName] || 0) + 1;
    }
  });

  // Get recently joined users (last 30 days)
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const { count: recentCount, error: recentError } = await supabase
    .from("profiles")
    .select("*", { count: "exact", head: true })
    .gte("registered_at", thirtyDaysAgo.toISOString());

  if (recentError) {
    console.error("Error fetching recent users:", recentError);
    throw recentError;
  }

  // Get today's new users
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const { count: todayCount, error: todayError } = await supabase
    .from("profiles")
    .select("*", { count: "exact", head: true })
    .gte("registered_at", today.toISOString())
    .lt("registered_at", tomorrow.toISOString());

  if (todayError) {
    console.error("Error fetching today's users:", todayError);
    throw todayError;
  }

  // Get yesterday's new users
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const { count: yesterdayCount, error: yesterdayError } = await supabase
    .from("profiles")
    .select("*", { count: "exact", head: true })
    .gte("registered_at", yesterday.toISOString())
    .lt("registered_at", today.toISOString());

  if (yesterdayError) {
    console.error("Error fetching yesterday's users:", yesterdayError);
    throw yesterdayError;
  }

  return {
    total: totalCount || 0,
    active: activeCount || 0,
    pending: pendingCount || 0,
    roles,
    recent: recentCount || 0,
    today: todayCount || 0,
    yesterday: yesterdayCount || 0,
  };
};

// User reading statistics interface
export interface UserReadingStats {
  bookViews: number;
  bookReads: number;
  audioPlays: number;
  totalMinutesRead: number;
  totalPagesRead: number;
  favoriteCategories: string[];
}

// Function to fetch user's selected categories from user_categories table
export const fetchUserCategories = async (userId: number): Promise<string[]> => {
  try {
    console.log("Fetching categories for user:", userId);
    
    // First get the category IDs for this user
    const { data: userCategoryData, error: userCategoryError } = await supabase
      .from("user_categories")
      .select("category_id")
      .eq("profile_id", userId);

    if (userCategoryError) {
      console.error("Error fetching user category IDs:", userCategoryError);
      throw userCategoryError;
    }

    console.log("User category IDs:", userCategoryData);

    if (!userCategoryData || userCategoryData.length === 0) {
      console.log("No categories found for user");
      return [];
    }

    // Extract category IDs
    const categoryIds = userCategoryData.map(item => item.category_id).filter(Boolean);
    
    if (categoryIds.length === 0) {
      console.log("No valid category IDs found");
      return [];
    }

    // Now fetch the category names
    const { data: categoriesData, error: categoriesError } = await supabase
      .from("categories")
      .select("id, name")
      .in("id", categoryIds);

    if (categoriesError) {
      console.error("Error fetching category names:", categoriesError);
      throw categoriesError;
    }

    console.log("Categories data:", categoriesData);

    const categoryNames = categoriesData?.map(cat => cat.name) || [];
    console.log("Final category names:", categoryNames);
    
    return categoryNames;
  } catch (error) {
    console.error("Error fetching user categories:", error);
    return [];
  }
};

// Function to fetch user's selected categories with IDs and names
export const fetchUserCategoriesWithIds = async (userId: number): Promise<Array<{id: number, name: string}>> => {
  try {
    console.log("Fetching categories with IDs for user:", userId);
    
    // First get the category IDs for this user
    const { data: userCategoryData, error: userCategoryError } = await supabase
      .from("user_categories")
      .select("category_id")
      .eq("profile_id", userId);

    if (userCategoryError) {
      console.error("Error fetching user category IDs:", userCategoryError);
      throw userCategoryError;
    }

    console.log("User category IDs:", userCategoryData);

    if (!userCategoryData || userCategoryData.length === 0) {
      console.log("No categories found for user");
      return [];
    }

    // Extract category IDs
    const categoryIds = userCategoryData.map(item => item.category_id).filter(Boolean);
    
    if (categoryIds.length === 0) {
      console.log("No valid category IDs found");
      return [];
    }

    // Now fetch the category names
    const { data: categoriesData, error: categoriesError } = await supabase
      .from("categories")
      .select("id, name")
      .in("id", categoryIds);

    if (categoriesError) {
      console.error("Error fetching category names:", categoriesError);
      throw categoriesError;
    }

    console.log("Categories data with IDs:", categoriesData);
    
    return categoriesData || [];
  } catch (error) {
    console.error("Error fetching user categories with IDs:", error);
    return [];
  }
};

// Function to fetch all available categories for selection
export const fetchAllCategories = async (): Promise<Array<{id: number, name: string}>> => {
  try {
    const { data, error } = await supabase
      .from("categories")
      .select("id, name")
      .eq("is_active", true)
      .order("name");

    if (error) {
      console.error("Error fetching all categories:", error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error("Error fetching all categories:", error);
    return [];
  }
};

// Function to add categories for a user - uses direct database query with RLS
export const addUserCategories = async (userId: number, categoryIds: number[]): Promise<void> => {
  try {
    console.log("Adding categories for user:", userId, "Categories:", categoryIds);

    // Get existing categories for this user to avoid duplicates
    const { data: existingCategories, error: fetchError } = await supabase
      .from("user_categories")
      .select("category_id")
      .eq("profile_id", userId);

    if (fetchError) {
      console.error("Error fetching existing categories:", fetchError);
      throw fetchError;
    }

    const existingCategoryIds = existingCategories?.map(item => item.category_id) || [];
    
    // Filter out categories that already exist
    const newCategoryIds = categoryIds.filter(categoryId => !existingCategoryIds.includes(categoryId));

    if (newCategoryIds.length === 0) {
      console.log("All categories already exist for this user");
      return; // No error, just nothing to add
    }

    // Add only the new categories
    const insertData = newCategoryIds.map((categoryId: number) => ({
      profile_id: userId,
      category_id: categoryId,
    }));

    const { error: insertError } = await supabase
      .from("user_categories")
      .insert(insertData);

    if (insertError) {
      console.error("Error adding categories:", insertError);
      throw insertError;
    }

    console.log(`Successfully added ${newCategoryIds.length} new categories`);
  } catch (error) {
    console.error("Error adding user categories:", error);
    throw error;
  }
};

// Function to remove a specific category for a user - uses direct database query with RLS
export const removeUserCategory = async (userId: number, categoryId: number): Promise<void> => {
  try {
    console.log("Removing category:", categoryId, "for user:", userId);

    const { error: deleteError } = await supabase
      .from("user_categories")
      .delete()
      .eq("profile_id", userId)
      .eq("category_id", categoryId);

    if (deleteError) {
      console.error("Error removing category:", deleteError);
      throw deleteError;
    }

    console.log("Successfully removed user category");
  } catch (error) {
    console.error("Error removing user category:", error);
    throw error;
  }
};

// Function to clear categories for a user - uses direct database query with RLS
export const clearUserCategories = async (userId: number): Promise<void> => {
  try {
    console.log("Clearing categories for user:", userId);

    const { error: deleteError } = await supabase
      .from("user_categories")
      .delete()
      .eq("profile_id", userId);

    if (deleteError) {
      console.error("Error clearing categories:", deleteError);
      throw deleteError;
    }

    console.log("Successfully cleared user categories");
  } catch (error) {
    console.error("Error clearing user categories:", error);
    throw error;
  }
};

// Get user reading statistics
export const fetchUserReadingStats = async (
  userId: number | string
): Promise<UserReadingStats> => {
  try {
    // Call the Edge Function to get reading statistics
    // This avoids TypeScript issues with direct table access
    const { data, error } = await supabase.functions.invoke(
      "users/reading-stats",
      {
        body: { userId },
      }
    );

    if (error) {
      throw new Error(error.message || "Failed to fetch reading statistics");
    }

    // Return the data from the Edge Function
    return {
      bookViews: data.bookViews || 0,
      bookReads: data.bookReads || 0,
      audioPlays: data.audioPlays || 0,
      totalMinutesRead: data.totalMinutesRead || 0,
      totalPagesRead: data.totalPagesRead || 0,
      favoriteCategories: data.favoriteCategories || [
        "Personal Development",
        "Psychology",
        "Business",
      ],
    };
  } catch (error) {
    console.error("Error fetching user reading stats:", error);

    // If there's an error, return default values since the database tables may not exist yet
    console.warn("Edge function failed, returning default reading stats");
    return {
      bookViews: 0,
      bookReads: 0,
      audioPlays: 0,
      totalMinutesRead: 0,
      totalPagesRead: 0,
      favoriteCategories: ["Personal Development", "Psychology", "Business"],
    };
  }
};

// Fetch all user profiles
export const fetchAllUsers = async (): Promise<User[]> => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .order("name", { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  // Transform the data to match User interface
  return (data || []).map((user) => ({
    id: user.id,
    uid: user.uid,
    name: user.name,
    email: user.email,
    avatarUrl: user.avatar_url,
    ageGroup: user.age_group,
    isActive: user.is_active,
    registeredAt: user.registered_at,
    lastLoginAt: null, // Not available in this query
    roleIds: [], // Not available in this query
    roles: [], // Not available in this query
  }));
};

// Search user profiles by name or email
export const searchUsers = async (query: string): Promise<User[]> => {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .order("name", { ascending: true })
      .limit(10);

    if (error) {
      console.error("Error searching users:", error);
      throw error;
    }

    return (data || []).map((user) => ({
      id: user.id,
      uid: user.uid,
      name: user.name,
      email: user.email,
      avatarUrl: user.avatar_url,
      isActive: user.is_active,
      registeredAt: user.registered_at,
      roleIds: [],
      roles: [],
      referralCode: user.referral_code,
      referredBy: user.referred_by,
    }));
  } catch (error) {
    console.error("Error in searchUsers:", error);
    throw error;
  }
};

// Fetch users with referral summary data
export const fetchUsersWithReferralData = async (
  userIds?: number[]
): Promise<User[]> => {
  try {
    let query = supabase
      .from("profiles")
      .select(
        `
        *,
        user_to_roles (
          role_id,
          user_roles (
            id, name, description, is_active, is_system
          )
        ),
        referrer:referred_by(name)
      `
      );

    if (userIds && userIds.length > 0) {
      query = query.in("id", userIds);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching users with referral data:", error);
      throw error;
    }

    return transformUserData(data || []);
  } catch (error) {
    console.error("Error in fetchUsersWithReferralData:", error);
    throw error;
  }
};

// Interface for account deletion feedback
export interface AccountDeletionFeedback {
  id: number;
  user_id: number | null;
  auth_uid: string | null;
  email: string;
  name: string | null;
  reason: string;
  additional_feedback: string | null;
  deletion_requested_at: string;
  user_agent: string | null;
  ip_address: string | null;
  app_version: string | null;
  platform: string | null;
  subscription_status: string | null;
  last_login_at: string | null;
  account_age_days: number | null;
  total_books_read: number | null;
  total_reading_time_minutes: number | null;
  created_at: string;
  updated_at: string;
}

// Fetch account deletion feedback for a user
export const fetchUserDeletionFeedback = async (
  userId: number,
  authUid?: string
): Promise<AccountDeletionFeedback | null> => {
  try {
    // Use typed call; if TS complains, we can cast the return payload shape only
    const { data, error } = await supabase
      .from("account_deletion_feedbacks")
      .select("*")
      .eq(userId ? "user_id" : "auth_uid", userId || authUid)
      .order("deletion_requested_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error fetching account deletion feedback:", error);
      throw error;
    }

    return data as AccountDeletionFeedback | null;
  } catch (error: unknown) {
    console.error("Error in fetchUserDeletionFeedback:", error);
    return null;
  }
};

// Add this new function to fetch user device information
export async function fetchUserDevices(userId: number): Promise<{
  id: number;
  platform: string;
  device_model: string | null;
  app_version: string | null;
  os_version: string | null;
  is_active: boolean;
  last_used_at: string;
  created_at: string;
  updated_at: string;
}[]> {
  try {
    const { data, error } = await supabase
      .from('user_push_tokens')
      .select(`
        id,
        platform,
        device_model,
        app_version,
        os_version,
        is_active,
        last_used_at,
        created_at,
        updated_at
      `)
      .eq('user_id', userId)
      .order('last_used_at', { ascending: false });

    if (error) {
      console.error('Error fetching user devices:', error);
      throw new Error(error.message);
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching user devices:', error);
    throw error;
  }
}
